import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash, PaperPlaneRight, ArrowCounterClockwise, Rocket, FloppyDisk, FileXls, FilePdf } from "@phosphor-icons/react";
import { RuleExamples } from "@/components/RuleExamples";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-DO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
function ExportButtons() {
  const [busy, setBusy] = useState(null);
  const dl = async (format) => {
    setBusy(format);
    try {
      const r = await api.get("/bot-training/export", { params: { format }, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `historial-reglas.${format}`;
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: name });
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("No se pudo exportar el historial");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex gap-2" data-testid="bt-export">
      <Button variant="outline" size="sm" className="gap-1" disabled={!!busy} onClick={() => dl("xlsx")} data-testid="bt-export-xlsx">
        <FileXls size={14} /> {busy === "xlsx" ? "Generando…" : "Historial Excel"}
      </Button>
      <Button variant="outline" size="sm" className="gap-1" disabled={!!busy} onClick={() => dl("pdf")} data-testid="bt-export-pdf">
        <FilePdf size={14} /> {busy === "pdf" ? "Generando…" : "Historial PDF"}
      </Button>
    </div>
  );
}

const area = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";

function FixedAnswers({ items, onChange }) {
  const set = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2" data-testid="bt-fixed-list">
      {items.map((it, i) => (
        <div key={i} className="border border-border rounded-md p-2 space-y-1.5 bg-background/50" data-testid={`bt-fixed-${i}`}>
          <div className="flex gap-2">
            <Input value={it.topic} onChange={(e) => set(i, "topic", e.target.value)} placeholder="Tema (ej. Horario de taller)" className="h-8 text-sm" data-testid={`bt-fixed-topic-${i}`} />
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`bt-fixed-remove-${i}`}><Trash size={14} /></Button>
          </div>
          <Input value={it.triggers || ""} onChange={(e) => set(i, "triggers", e.target.value)} placeholder="Cómo lo preguntan (opcional): a qué hora abren, horario…" className="h-8 text-xs" data-testid={`bt-fixed-triggers-${i}`} />
          <textarea value={it.answer} onChange={(e) => set(i, "answer", e.target.value)} rows={2} placeholder="Respuesta exacta" className={area} data-testid={`bt-fixed-answer-${i}`} />
        </div>
      ))}
      <Button size="sm" variant="outline" className="gap-1" onClick={() => onChange([...items, { topic: "", triggers: "", answer: "" }])} data-testid="bt-fixed-add"><Plus size={12} /> Agregar respuesta fija</Button>
    </div>
  );
}

function EscalateTopics({ items, onChange }) {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onChange([...items, v.trim()]); setV(""); } };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" data-testid="bt-escalate-list">
        {items.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-xs">
            {t}<button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`bt-escalate-remove-${i}`}><Trash size={10} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Ej. Reclamos legales, precios de flotilla…" className="h-8 text-sm" data-testid="bt-escalate-input" />
        <Button size="sm" variant="outline" onClick={add} data-testid="bt-escalate-add">Agregar</Button>
      </div>
    </div>
  );
}

function TestChat({ inject }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ask = async (m, history, expected) => {
    setMsgs((p) => [...p, { role: "user", content: m }]); setText(""); setBusy(true);
    try {
      const { data } = await api.post("/bot-training/test", { message: m, history });
      setMsgs((p) => [...p, { role: "bot", content: data.reply || "(sin respuesta)", tools: data.tools_simulated, expected }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error en la prueba"); } finally { setBusy(false); }
  };
  const send = () => {
    const m = text.trim();
    if (!m || busy) return;
    ask(m, msgs.map((x) => ({ role: x.role, content: x.content })));
  };
  useEffect(() => {
    if (!inject) return;
    setMsgs([]);
    ask(inject.cliente, [], inject.bot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inject]);
  return (
    <div className="border border-sky-500/30 bg-sky-500/5 rounded-lg flex flex-col h-[560px]" data-testid="bt-test-chat">
      <div className="px-4 py-3 border-b border-sky-500/20 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Probar borrador</div>
          <div className="text-[11px] text-muted-foreground">Usa el borrador guardado. No se crean citas ni leads; los clientes no lo ven.</div>
        </div>
        <Button size="sm" variant="ghost" className="text-xs" onClick={() => setMsgs([])} data-testid="bt-test-reset">Reiniciar</Button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="bt-test-messages">
        {msgs.length === 0 && <div className="text-xs text-muted-foreground text-center mt-8">Escribe como si fueras un cliente.</div>}
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
            {m.content}
            {m.tools?.length > 0 && <div className="text-[10px] opacity-70 mt-1">Acciones simuladas: {m.tools.join(", ")}</div>}
            {m.expected && (
              <div className="mt-2 pt-2 border-t border-border text-[11px] text-muted-foreground" data-testid={`bt-test-expected-${i}`}>
                <b>Ejemplo del historial:</b> {m.expected}
              </div>
            )}
          </div>
        ))}
        {busy && <div className="text-xs text-muted-foreground">El bot está escribiendo…</div>}
      </div>
      <div className="p-3 border-t border-sky-500/20 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Mensaje del cliente…" data-testid="bt-test-input" />
        <Button onClick={send} disabled={busy} data-testid="bt-test-send"><PaperPlaneRight size={14} /></Button>
      </div>
    </div>
  );
}

export default function BotTrainingPanel() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);
  const [inject, setInject] = useState(null);
  const load = async () => {
    const { data: d } = await api.get("/bot-training");
    setData(d); setDraft(d.draft);
  };
  useEffect(() => { load().catch(() => toast.error("No se pudo cargar")); }, []);
  if (!draft) return null;
  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async (quiet) => {
    setBusy("save");
    try {
      await api.put("/bot-training/draft", draft);
      if (!quiet) toast.success("Borrador guardado");
      await load();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); return false; } finally { setBusy(null); }
  };
  const publish = async () => {
    const note = window.prompt("Describe el cambio (opcional). Se aplicará a todos los canales en vivo:");
    if (note === null) return;
    if (!(await save(true))) return;
    setBusy("publish");
    try {
      const { data: v } = await api.post("/bot-training/publish", { note });
      toast.success(`Versión ${v.version} publicada — el bot ya responde con estas reglas`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al publicar"); } finally { setBusy(null); }
  };
  const rollback = async (v) => {
    if (!window.confirm(`¿Volver a la versión ${v.version}? Se aplicará en vivo y reemplazará el borrador.`)) return;
    try { await api.post(`/bot-training/rollback/${v.id}`); toast.success(`Versión ${v.version} activa`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const active = data.active;

  return (
    <div className="space-y-4" data-testid="bot-training-panel">
      <div className="border border-border rounded-lg bg-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-semibold">Entrenamiento del bot</div>
          <div className="text-xs text-muted-foreground">Reglas que se suman a las instrucciones base del bot en WhatsApp, Messenger, Instagram y Web. No cambian las herramientas ni el saludo oficial.</div>
        </div>
        <ExportButtons />
        <Badge variant="outline" data-testid="bt-active-version">{active ? `En vivo: versión ${active.version} · ${fmt(active.published_at)}` : "Sin reglas publicadas"}</Badge>
        <Button variant="outline" size="sm" className="gap-1" disabled={!!busy} onClick={() => save(false)} data-testid="bt-save"><FloppyDisk size={14} /> {busy === "save" ? "Guardando…" : "Guardar borrador"}</Button>
        <Button size="sm" className="gap-1" disabled={!!busy} onClick={publish} data-testid="bt-publish"><Rocket size={14} /> {busy === "publish" ? "Publicando…" : "Publicar"}</Button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="space-y-4">
          <section className="border border-border rounded-lg bg-card p-4 space-y-2">
            <div className="text-sm font-semibold">1 · Reglas y tono</div>
            <textarea value={draft.rules} onChange={(e) => patch("rules", e.target.value)} rows={7} maxLength={8000} className={area}
              placeholder={"Ej.:\n- Responde en máximo 3 oraciones.\n- Siempre ofrece una prueba de manejo cuando pregunten por un vehículo.\n- No menciones descuentos que no estén en la Base de Conocimiento."} data-testid="bt-rules" />
          </section>
          <section className="border border-border rounded-lg bg-card p-4 space-y-2">
            <div className="text-sm font-semibold">2 · Respuestas fijas por tema</div>
            <div className="text-[11px] text-muted-foreground">Cuando el cliente pregunte por el tema, el bot responderá exactamente este texto.</div>
            <FixedAnswers items={draft.fixed_answers} onChange={(v) => patch("fixed_answers", v)} />
          </section>
          <section className="border border-border rounded-lg bg-card p-4 space-y-2">
            <div className="text-sm font-semibold">3 · Temas prohibidos / pasar a un asesor</div>
            <div className="text-[11px] text-muted-foreground">El bot no responde el fondo y transfiere la conversación a un asesor.</div>
            <EscalateTopics items={draft.escalate_topics} onChange={(v) => patch("escalate_topics", v)} />
          </section>
        </div>
        <div className="space-y-4">
          <TestChat inject={inject} />
          <RuleExamples onTry={(ex) => setInject({ ...ex })} />
          <section className="border border-border rounded-lg bg-card" data-testid="bt-versions">
            <div className="px-4 py-2.5 border-b border-border text-sm font-semibold">Historial de versiones</div>
            <div className="divide-y divide-border max-h-64 overflow-y-auto">
              {data.versions.length === 0 && <div className="px-4 py-3 text-xs text-muted-foreground">Aún no hay versiones publicadas.</div>}
              {data.versions.map((v) => (
                <div key={v.id} className="px-4 py-2 flex items-center gap-2 text-xs" data-testid={`bt-version-${v.version}`}>
                  <b>v{v.version}</b>
                  <span className="text-muted-foreground truncate flex-1">{v.note || "Sin nota"} · {v.published_by_name || "—"} · {fmt(v.published_at)}</span>
                  {active?.id === v.id ? <Badge className="bg-emerald-600 text-white text-[10px]">En vivo</Badge> : (
                    <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => rollback(v)} data-testid={`bt-rollback-${v.version}`}><ArrowCounterClockwise size={12} /> Restaurar</Button>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
